import { Inject, Injectable, Optional } from "@angular/core";
import {
	MatLegacySnackBar as MatSnackBar,
	MatLegacySnackBarRef as MatSnackBarRef,
} from "@angular/material/legacy-snack-bar";
import { Store } from "@ngrx/store";
import {
	JurisdictionPrefixPipe,
	SnackBarCacheService,
	AbstractTranslatableSnackBarService,
	TranslationGroup as AbstractSnackbarTranslationGroup,
} from "@obg/ngx.core";
import { ButtonContent, CoreContentState, TranslationGroup, TranslationSelector } from "@obg/ngx.core.content";
import { isUndefined, omitBy } from "lodash-es";
import { EMPTY, Observable, of, Subscription } from "rxjs";
import { map, switchMap } from "rxjs/operators";

import { MAT_CORE_CONFIG } from "../const";
import { MatCoreConfig } from "../model";

import { SnackBarComponent } from "./snackbar.component";
import { SnackBarComponentBase, SnackBarOptions } from "./snackbar.model";

@Injectable()
export class TranslatableSnackBarService extends AbstractTranslatableSnackBarService {
	private isGeneralTranslationGroupNotUsed: boolean | undefined;

	constructor(
		@Optional() @Inject(MAT_CORE_CONFIG) coreConfig: MatCoreConfig,
		private snackBar: MatSnackBar,
		private store: Store<CoreContentState>,
		private translationSelector: TranslationSelector,
		private cacheService: SnackBarCacheService,
		private jurisdictionPrefixPipe: JurisdictionPrefixPipe
	) {
		super();
		this.isGeneralTranslationGroupNotUsed = coreConfig?.doNotUseGeneralTranslationGroup;
	}

	/**
	 * @deprecated it is safer to use openSnackBar$ directly, this method won't be supported in future versions
	 */
	openSnackBar(
		config: SnackBarOptions,
		translationGroups: TranslationGroup[] = [],
		interpolateParams?: object | undefined
	): Subscription {
		return this.openSnackBar$(config, translationGroups, interpolateParams).subscribe();
	}

	openSnackBar$(
		config: SnackBarOptions,
		translationGroups: TranslationGroup[] | AbstractSnackbarTranslationGroup[] = [],
		interpolateParams?: object | undefined
	): Observable<MatSnackBarRef<SnackBarComponentBase>> {
		const keys = this.getKeys(config);
		let panelClass = config.type ? [config.type.toString()] : [];

		if (config.panelClass) {
			panelClass = [...config.panelClass, ...panelClass];
		}

		return (config.id ? this.cacheService.getSnackbarDoNotShowAgain$(config.id) : of(null)).pipe(
			switchMap((doNotShow) => {
				if (doNotShow) {
					return EMPTY;
				}

				return this.store.pipe(
					this.translationSelector.getByKeys$(
						keys,
						this.isGeneralTranslationGroupNotUsed
							? translationGroups
							: [...translationGroups, TranslationGroup.General],
						interpolateParams
					),
					map(([message, action, ...buttons]) => {
						const translatedButtons: ButtonContent[] = [];
						const component = config.snackBarComponent || SnackBarComponent;

						if (config.buttons) {
							for (let i = 0; i < config.buttons.length; i++) {
								config.buttons[i].text = buttons[i];
								translatedButtons.push(config.buttons[i]);
							}
						}

						let options = omitBy(
							{
								data: {
									message,
									icon: config.icon,
									action,
									buttons: translatedButtons,
									type: config.type,
									doNotShowAgainButton: config.doNotShowAgainButton,
									dismissible: config.dismissible,
									dismissibleDuration: config.dismissibleDuration,
									dismissibleDurationTranslationPrefix: config.dismissibleDurationTranslationPrefix,
									id: config.id,
								},
								verticalPosition: config.verticalPosition || "top",
								duration: config.duration,
								panelClass,
							},
							isUndefined
						);

						if (config.keepOpen) {
							options = {
								...options,
								duration: undefined,
							};
						}

						return this.snackBar.openFromComponent(component, options);
					})
				);
			})
		);
	}

	dismissSnackBar() {
		this.snackBar.dismiss();
	}

	getSnackbarId(): string | null | undefined {
		return this.snackBar._openedSnackBarRef?.containerInstance.snackBarConfig.data?.id;
	}

	private getKeys({ buttons, message, prefixJurisdiction, action = "" }: SnackBarOptions): string[] {
		const keys = [message.toLowerCase(), action];
		if (buttons) {
			keys.push(...buttons.map((button) => button.text || ""));
		}
		return keys.map((key) => (key && prefixJurisdiction ? this.prefixJurisdiction(key) : key));
	}

	private prefixJurisdiction(key: string): string {
		return this.jurisdictionPrefixPipe.transform(key);
	}
}
